import { useState, useEffect, useCallback } from "react";

interface User {
  id: string;
  name: string;
  position_count: number;
}

interface Trip {
  id: string;
  name: string;
  position_count: number;
}

const th: React.CSSProperties = {
  textAlign: "left",
  borderBottom: "2px solid #ddd",
  padding: "8px 12px",
  fontWeight: 600,
};
const td: React.CSSProperties = { padding: "8px 12px", borderBottom: "1px solid #f0f0f0" };
const inp: React.CSSProperties = { padding: "6px 10px", border: "1px solid #ccc", borderRadius: 4, minWidth: 130 };
const btn: React.CSSProperties = {
  padding: "6px 14px",
  background: "#333",
  color: "#fff",
  border: "none",
  borderRadius: 4,
  cursor: "pointer",
};
const btnDanger: React.CSSProperties = {
  background: "none",
  border: "none",
  color: "#c00",
  cursor: "pointer",
  fontSize: 13,
};

export default function AdminView() {
  const [token, setToken] = useState(() => localStorage.getItem("admin_token") ?? "");
  const [authed, setAuthed] = useState(false);
  const [users, setUsers] = useState<User[]>([]);
  const [trips, setTrips] = useState<Record<string, Trip[]>>({});
  const [error, setError] = useState<string | null>(null);

  const [password, setPassword] = useState("");

  const [newUserId, setNewUserId] = useState("");
  const [newUserName, setNewUserName] = useState("");

  const [tripUser, setTripUser] = useState("");
  const [newTripId, setNewTripId] = useState("");
  const [newTripName, setNewTripName] = useState("");

  const headers = useCallback(
    () => ({ Authorization: `Bearer ${token}`, "Content-Type": "application/json" }),
    [token]
  );

  const loadUsers = useCallback(async (tok: string) => {
    const h = { Authorization: `Bearer ${tok}`, "Content-Type": "application/json" };
    const resp = await fetch("/api/admin/users", { headers: h });
    if (!resp.ok) { setAuthed(false); return; }
    const data: User[] = await resp.json();
    setUsers(data);
    setAuthed(true);
    const tripMap: Record<string, Trip[]> = {};
    await Promise.all(
      data.map(async (u) => {
        const r = await fetch(`/api/admin/users/${u.id}/trips`, { headers: h });
        if (r.ok) tripMap[u.id] = await r.json();
      })
    );
    setTrips(tripMap);
  }, []);

  async function login(e: React.FormEvent) {
    e.preventDefault();
    localStorage.setItem("admin_token", password);
    setToken(password);
    await loadUsers(password);
  }

  useEffect(() => {
    if (token) loadUsers(token);
  }, []); // only on mount — token changes are handled explicitly

  async function createUser(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    const resp = await fetch("/api/admin/users", {
      method: "POST",
      headers: headers(),
      body: JSON.stringify({ id: newUserId, name: newUserName }),
    });
    if (!resp.ok) { setError(await resp.text()); return; }
    setNewUserId("");
    setNewUserName("");
    loadUsers(token);
  }

  async function createTrip(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    const resp = await fetch(`/api/admin/users/${tripUser}/trips`, {
      method: "POST",
      headers: headers(),
      body: JSON.stringify({ id: newTripId, name: newTripName }),
    });
    if (!resp.ok) { setError(await resp.text()); return; }
    setNewTripId("");
    setNewTripName("");
    loadUsers(token);
  }

  async function deleteUser(id: string) {
    if (!confirm(`Delete user "${id}"? This only works if they have no positions.`)) return;
    setError(null);
    const resp = await fetch(`/api/admin/users/${id}`, { method: "DELETE", headers: headers() });
    if (!resp.ok) { setError(await resp.text()); return; }
    loadUsers(token);
  }

  async function deleteTrip(user: string, trip: string) {
    if (!confirm(`Delete trip "${trip}"? This only works if it has no positions.`)) return;
    setError(null);
    const resp = await fetch(`/api/admin/users/${user}/trips/${trip}`, {
      method: "DELETE",
      headers: headers(),
    });
    if (!resp.ok) { setError(await resp.text()); return; }
    loadUsers(token);
  }

  function logout() {
    localStorage.removeItem("admin_token");
    setToken("");
    setAuthed(false);
  }

  if (!authed) {
    return (
      <div style={{ padding: 48, maxWidth: 360, margin: "0 auto" }}>
        <h2 style={{ marginBottom: 20 }}>Admin login</h2>
        <form onSubmit={login} style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          <input
            type="password"
            placeholder="Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            style={{ ...inp, width: "100%", boxSizing: "border-box" }}
            autoFocus
          />
          <button type="submit" style={btn}>Login</button>
        </form>
      </div>
    );
  }

  return (
    <div style={{ padding: 40, maxWidth: 900, margin: "0 auto", fontFamily: "sans-serif" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 28 }}>
        <h2 style={{ margin: 0 }}>Admin</h2>
        <button onClick={logout} style={{ ...btn, background: "#888" }}>Logout</button>
      </div>

      {error && (
        <div style={{ background: "#fee", border: "1px solid #fcc", padding: "10px 14px", borderRadius: 4, marginBottom: 16, display: "flex", justifyContent: "space-between" }}>
          <span>{error}</span>
          <button onClick={() => setError(null)} style={{ background: "none", border: "none", cursor: "pointer" }}>✕</button>
        </div>
      )}

      <h3>Users &amp; trips</h3>
      <table style={{ width: "100%", borderCollapse: "collapse", marginBottom: 32 }}>
        <thead>
          <tr>
            <th style={th}>User ID</th>
            <th style={th}>Name</th>
            <th style={th}>Trips</th>
            <th style={th}></th>
          </tr>
        </thead>
        <tbody>
          {users.map((u) => (
            <tr key={u.id}>
              <td style={td}>
                <code>{u.id}</code>
              </td>
              <td style={td}>{u.name}</td>
              <td style={td}>
                {(trips[u.id] ?? []).map((t) => (
                  <div key={t.id} style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 4 }}>
                    <a href={`/${u.id}/${t.id}`} style={{ fontFamily: "monospace" }}>{t.id}</a>
                    <span style={{ color: "#666", fontSize: 13 }}>{t.name} · {t.position_count} positions</span>
                    <button onClick={() => deleteTrip(u.id, t.id)} style={btnDanger} title="Delete trip">
                      Remove
                    </button>
                  </div>
                ))}
              </td>
              <td style={{ ...td, whiteSpace: "nowrap" }}>
                <span style={{ color: "#999", fontSize: 13, marginRight: 12 }}>{u.position_count} pos</span>
                <button onClick={() => deleteUser(u.id)} style={btnDanger}>Delete user</button>
              </td>
            </tr>
          ))}
          {users.length === 0 && (
            <tr>
              <td colSpan={4} style={{ ...td, color: "#999" }}>No users yet.</td>
            </tr>
          )}
        </tbody>
      </table>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 40 }}>
        <section>
          <h3 style={{ marginTop: 0 }}>Add user</h3>
          <form onSubmit={createUser} style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            <input
              placeholder="ID (URL slug, e.g. bjorn)"
              value={newUserId}
              onChange={(e) => setNewUserId(e.target.value)}
              style={inp}
              required
            />
            <input
              placeholder="Display name"
              value={newUserName}
              onChange={(e) => setNewUserName(e.target.value)}
              style={inp}
              required
            />
            <button type="submit" style={{ ...btn, alignSelf: "flex-start" }}>Add user</button>
          </form>
        </section>

        <section>
          <h3 style={{ marginTop: 0 }}>Add trip</h3>
          <form onSubmit={createTrip} style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            <select
              value={tripUser}
              onChange={(e) => setTripUser(e.target.value)}
              style={inp}
              required
            >
              <option value="">Select user</option>
              {users.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.name} ({u.id})
                </option>
              ))}
            </select>
            <input
              placeholder="ID (URL slug, e.g. norway-2027)"
              value={newTripId}
              onChange={(e) => setNewTripId(e.target.value)}
              style={inp}
              required
            />
            <input
              placeholder="Display name"
              value={newTripName}
              onChange={(e) => setNewTripName(e.target.value)}
              style={inp}
              required
            />
            <button type="submit" style={{ ...btn, alignSelf: "flex-start" }}>Add trip</button>
          </form>
        </section>
      </div>
    </div>
  );
}
